import React, { useState } from 'react';

interface TableComponentProps {
  data: string[];
  answerKey: ('check' | 'cross' | ('check' | 'cross')[])[];
}

export const TableQAComponent: React.FC<TableComponentProps> = ({ data, answerKey }) => {
  const [userResponses, setUserResponses] = useState<('check' | 'cross' | null)[]>(
    Array(data.length).fill(null),
  );
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = userResponses.every((response) => response !== null); // ✅ Check if all answers are selected

  const handleSelection = (index: number, choice: 'check' | 'cross') => {
    if (!submitted) {
      const updatedResponses = [...userResponses];
      updatedResponses[index] = choice;
      setUserResponses(updatedResponses);
    }
  };

  const handleSubmit = () => setSubmitted(true);

  const handleRetake = () => {
    setUserResponses(Array(data.length).fill(null));
    setSubmitted(false);
  };

  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');
      window.print();
      printArea.classList.remove('print-view');
    }
  };

  return (
    <div id="print-view" className="w-full max-w-4xl bg-purple-100 shadow-md rounded-lg p-4">
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-purple-200">
            <th className="border border-gray-300 px-4 py-2">#</th>
            <th className="border border-gray-300 px-4 py-2">Task</th>
            <th className="border border-gray-300 px-4 py-2">Your Response</th>
            {submitted && <th className="border border-gray-300 px-4 py-2">Answer Key</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index} className="text-left">
              <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
              <td className="border border-gray-300 px-4 py-2">{item}</td>
              <td className="border border-gray-300 px-4 py-2 text-xl text-center">
                <span
                  className={`cursor-pointer px-2 ${
                    userResponses[index] === 'check' ? 'text-green-600 font-bold' : ''
                  }`}
                  onClick={() => handleSelection(index, 'check')}
                >
                  {userResponses[index] === 'check' ? '✔' : '○'}
                </span>
                |
                <span
                  className={`cursor-pointer px-2 ${
                    userResponses[index] === 'cross' ? 'text-red-600 font-bold' : ''
                  }`}
                  onClick={() => handleSelection(index, 'cross')}
                >
                  {userResponses[index] === 'cross' ? '❌' : '○'}
                </span>
              </td>
              {submitted && (
                <td className="border border-gray-300 px-4 py-2 text-center font-bold">
                  {Array.isArray(answerKey[index]) ? (
                    <div className="flex justify-center items-center gap-2">
                      {answerKey[index].includes('check') && (
                        <span className="text-green-600">✔</span>
                      )}
                      {answerKey[index].includes('cross') && (
                        <span className="text-red-600">❌</span>
                      )}
                    </div>
                  ) : (
                    <span
                      className={answerKey[index] === 'check' ? 'text-green-600' : 'text-red-600'}
                    >
                      {answerKey[index] === 'check' ? '✔' : '❌'}
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 flex justify-center gap-4">
        {!submitted ? (
          <button
            className={`px-4 py-2 rounded-md transition ${
              allAnswered
                ? 'bg-purple-500 text-white hover:bg-purple-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleSubmit}
            disabled={!allAnswered} // ✅ Disable if not all questions are answered
          >
            Answer Key
          </button>
        ) : (
          <>
            <button
              className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition"
              onClick={handleRetake}
            >
              Retake
            </button>
            <button
              className="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition"
              onClick={handlePrint}
            >
              Print
            </button>
          </>
        )}
      </div>
    </div>
  );
};
